import { useAnalysisStatus } from '../../api/hooks';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { darkTheme, FONT } from '../../theme/tokens';

const t = darkTheme;

/**
 * Persistent footer that surfaces analysis state across every route. Hidden
 * when the active catalog is idle; visible while analysis is running or has
 * failed. Mounted at the App shell level so it travels with the user no
 * matter what tab they're on (SC-1871).
 *
 * Subscribes to useActiveCatalog so switching catalogs from the sidebar
 * picker (SC-1877) re-targets this bar at the newly-picked catalog.
 */
export function AnalysisStatusBar() {
  const { active } = useActiveCatalog();
  const status = useAnalysisStatus(active?.id ?? '');

  // Show only while analysis is in flight or after a failure — `not_started`
  // (no analysis triggered) and `complete` (already done) are silent states.
  if (!active || status.state === 'not_started' || status.state === 'complete') {
    return null;
  }

  const isFailed = status.state === 'failed';

  // Inert status banner — it used to link to the findings inbox, but that
  // surface was retired in SC-2427 (it crashed on the post-SC-2291 finding
  // shape). Restore the link target when the inbox is rebuilt; for now this
  // just communicates analysis progress without a click-through to a 404.
  return (
    <div
      data-testid="analysis-status-bar"
      style={{
        // Laid out as a flex child of the App shell — takes vertical space
        // instead of overlaying, so sidebar profile + agent chat input
        // don't get clipped underneath.
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 16,
        padding: '8px 24px',
        background: t.surface,
        borderTop: `1px solid ${t.border}`,
        fontFamily: FONT,
        fontSize: 12,
        color: t.text,
        boxShadow: '0 -2px 12px rgba(0,0,0,0.25)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: isFailed ? t.red : t.blue,
            animation: isFailed ? undefined : 'ekomPulse 2s ease-in-out infinite',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        {isFailed ? (
          <span style={{ color: t.redText }}>
            Analysis failed for {active.name} — re-upload to try again
          </span>
        ) : (
          <span>
            Analyzing {active.name}
            <AnimatedEllipsis />
          </span>
        )}
      </span>
    </div>
  );
}

function AnimatedEllipsis() {
  // Three dots staggered by 200ms each — reads as a soft typing-style indicator.
  const dot = {
    display: 'inline-block',
    animation: 'ekomPulse 1.4s ease-in-out infinite',
  };
  return (
    <span aria-hidden="true">
      <span style={{ ...dot }}>.</span>
      <span style={{ ...dot, animationDelay: '0.2s' }}>.</span>
      <span style={{ ...dot, animationDelay: '0.4s' }}>.</span>
    </span>
  );
}
